/**
 * Download config · 多游戏投放对比页（Competitor Ad Comparison）
 *
 * 3 个下载 key（与有 hasDownload 的 3 个模块一一对应）：
 * - MarketingCompetitorAdComparisonTrend     投放趋势（数据表视图：行=游戏，列=日期）
 * - MarketingCompetitorAdComparisonChannel   渠道表现排名（5 列；Channel Share 列条件展示）
 * - MarketingCompetitorAdComparisonCountry   分国家对比（行=国家，每个游戏一列 + Total）
 *
 * 设计要点：
 * - **业务组件直接调 `gotoDownload`**，传入接口原始数据 + 元信息，不再有中间 trigger 工具层
 * - **本配置内做完整的 raw → 扁平 rows + 动态 columns 转换**：
 *   - `dealWithDataFunc({ data, auxiliaryParams })` 把原始接口数据扁平化为 Excel 行
 *   - `columns({ filter, auxiliaryParams })` 函数式动态返回列（日期 / 游戏列数量随 state 变）
 * - **type 默认 xlsx**：单 sheet 写入，不分页拉接口
 * - **数值保持原始类型 + numFmt**：百分比列 0~1 小数 + `numFmt: '0.00%'`，
 *   Excel 自动渲染为百分比格式且仍是数值类型（便于排序 / 筛选 / 透视）
 * - **国家代码展开为全名**：`countryMap[code].country_name` 在 dealWithDataFunc 内做
 *   （新窗口 React 实例无法访问原页面 cond 数据，必须 trigger 时通过 auxiliaryParams 传入）
 */
import {
  CommonColumnsType,
  DownloadConfigInfoType,
  DownloadParamsType,
} from '@op/pages/download/types';
import { channelInfo, ChannelIconType } from '@op/components/ChannelLabel';
import type {
  IChannelChartBarItem,
  IChannelRankingItem,
} from '@opm/pages/CompetitorAdComparison/types/channel';
import type { ICountryRowItem } from '@opm/pages/CompetitorAdComparison/types/country';
import type { ITrendSeries } from '@opm/pages/CompetitorAdComparison/types/trend';

// ─────────────────────────────────────────────────────────
// 共享 helper
// ─────────────────────────────────────────────────────────

/** game_id → game_name（auxiliaryParams.gameMap 字典） */
function gameName(gameMap: Record<string, any> = {}, gameId: string): string {
  return gameMap?.[gameId]?.game_name || gameId;
}

// ─────────────────────────────────────────────────────────
// Module A · 投放趋势对比（数据表：行=游戏，列=日期 + Total）
// ─────────────────────────────────────────────────────────
//
// 调用方传入：
//   data: ITrendSeries[]  —— hook 已转换好的友好结构
//   auxiliaryParams: { gameMap, metricLabel? }
//
export const MarketingCompetitorAdComparisonTrend: DownloadConfigInfoType = {
  columns: (item: DownloadParamsType): CommonColumnsType => {
    const series: ITrendSeries[] = item?.data || [];
    const dates: string[] = series[0]?.values?.map(v => v.date) ?? [];
    return [
      { key: 'game_name', header: 'Game' },
      ...dates.map(d => ({ key: `d_${d}`, header: d })),
    ];
  },
  dealWithDataFunc: ({ data, auxiliaryParams }: DownloadParamsType) => {
    const series: ITrendSeries[] = data || [];
    const gameMap = auxiliaryParams?.gameMap;
    return series.map(s => {
      const row: Record<string, any> = {
        game_name: gameName(gameMap, s.game_id),
      };
      s.values.forEach(point => {
        const v = Number.isFinite(point.value) ? point.value : 0;
        row[`d_${point.date}`] = v;
      });
      return row;
    });
  },
};

// ─────────────────────────────────────────────────────────
// Module B · 渠道表现排名表（行=游戏、列=5 metrics）
// ─────────────────────────────────────────────────────────
//
// 调用方传入：
//   data: IChannelRankingItem[]  —— useChannelRanking.fullList（已排序的全量）
//   auxiliaryParams: { gameMap, hasChannelFilter }
//   - hasChannelFilter: 是否筛选了具体渠道（决定 Channel Share 列是否展示）
//
// 列结构（与表格 §4.4.5 一致）：
//   Game / Ad Creatives / [Channel Share]? / Competitor Share / Score / Impressions
//
export const MarketingCompetitorAdComparisonChannel: DownloadConfigInfoType = {
  columns: (item: DownloadParamsType): CommonColumnsType => {
    const hasChannelFilter = !!item?.auxiliaryParams?.hasChannelFilter;
    const cols: CommonColumnsType = [
      { key: 'game_name', header: 'Game' },
      { key: 'creative_count_stat', header: 'Ad Creatives' },
    ];
    // Channel Share 仅在筛选了具体渠道时展示（百分比无意义时隐藏）
    if (hasChannelFilter) {
      cols.push({
        key: 'creative_count_stat_by_channel_percent',
        header: 'Active Ads Channel Share',
        // 0~1 小数 → Excel 渲染为 0.00% 百分比格式（保持数值类型）
        numFmt: '0.00%',
      });
    }
    cols.push(
      {
        key: 'creative_count_stat_by_competitor_percent',
        header: 'Active Ads Competitor Share',
        numFmt: '0.00%',
      },
      { key: 'creative_score_stat', header: 'Active Estimated Heat' },
      {
        key: 'impression_stat',
        header: 'Active Ads Impressions',
      },
    );
    return cols;
  },
  dealWithDataFunc: ({ data, auxiliaryParams }: DownloadParamsType) => {
    const list: IChannelRankingItem[] = data || [];
    const gameMap = auxiliaryParams?.gameMap;
    // 直接 spread 全字段；createExcel 只读 columns 里出现的 key（hasChannelFilter
    // 控制可见性即可，无需在 dealWithDataFunc 内条件 push）
    return list.map(row => ({
      ...row,
      game_name: gameName(gameMap, row.game_id),
    }));
  },
};

// ─────────────────────────────────────────────────────────
// Module B (table 视图) · 渠道矩阵表（行=游戏、列=渠道）
// ─────────────────────────────────────────────────────────
//
// 用于 ChannelCompare chartType=table 时的下载（与上方矩阵表完全一致）。
//
// 调用方传入：
//   data: IChannelChartBarItem[]  —— useChannelSummary.barList 输出
//   auxiliaryParams: { gameMap, metric, metricLabel?, matrixSort? }
//   - metric:     决定百分比列 numFmt（is *_percent 时 '0.00%'）
//   - matrixSort: 用户在矩阵表里点击的排序状态 { by: channel_id, order: 'asc'|'desc' }
//                 by 为空 → 不排序，使用首次出现顺序（与表格默认完全一致）
//
// 列结构：Game / <渠道 1> / <渠道 2> / ...
//
export const MarketingCompetitorAdComparisonChannelMatrix: DownloadConfigInfoType =
  {
    columns: (item: DownloadParamsType): CommonColumnsType => {
      const list: IChannelChartBarItem[] = item?.data || [];
      const metric: string = item?.auxiliaryParams?.metric || '';
      // 渠道顺序 = barList 中渠道首次出现顺序（与表格一致）
      const channelIds = list.map(c => c.channel_id);
      // 百分比 metric 时数值列加 numFmt
      const isPercent = metric.endsWith('_percent');
      const numFmt = isPercent ? '0.00%' : undefined;
      return [
        { key: 'game_name', header: 'Game' },
        ...channelIds.map(cid => ({
          key: `ch_${cid}`,
          header:
            channelInfo(cid as ChannelIconType, undefined, true)?.name || cid,
          numFmt,
        })),
      ];
    },
    dealWithDataFunc: ({ data, auxiliaryParams }: DownloadParamsType) => {
      const list: IChannelChartBarItem[] = data || [];
      const gameMap = auxiliaryParams?.gameMap;
      const matrixSort = auxiliaryParams?.matrixSort as
        | { by?: string; order?: 'asc' | 'desc' }
        | undefined;

      // 转置：barList → 行=游戏（保持游戏首次出现顺序）
      const map = new Map<string, Record<string, any>>();
      list.forEach(c => {
        c.games.forEach(g => {
          const v = Number.isFinite(g.value) ? g.value : 0;
          let row = map.get(g.game_id);
          if (!row) {
            row = {
              game_name: gameName(gameMap, g.game_id),
            };
            map.set(g.game_id, row);
          }
          row[`ch_${c.channel_id}`] = v;
        });
      });
      const rows = Array.from(map.values());

      // 应用矩阵表的当前排序（与表格展示完全一致）
      const sortBy = matrixSort?.by;
      if (sortBy) {
        const dir = matrixSort?.order === 'asc' ? 1 : -1;
        const key = `ch_${sortBy}`;
        rows.sort((a, b) => {
          const av = (a[key] as number) ?? 0;
          const bv = (b[key] as number) ?? 0;
          return (av - bv) * dir;
        });
      }
      return rows;
    },
  };

// ─────────────────────────────────────────────────────────
// Module C · 分国家对比（行=国家，每个游戏一列 + Total）
// ─────────────────────────────────────────────────────────
//
// 调用方传入：
//   data: ICountryRowItem[]      —— hook 已排序的全量 list（USA 置顶 + 用户排序）
//   auxiliaryParams: { gameMap, gameOrder, countryMap? }
//   - countryMap: 可选；提供时国家列展开为 full name，缺省则 alpha-2 大写
//
// 列结构：Country / <game 1> / <game 2> / ... / Total（与 stacked 视图一致；columns 视图无 Total 列也无害）
//
// ⚠️ 为什么没用 `countryRenderHelper`？
//   `countryRenderHelper(item, key) => dealWithOthersName(item[key], 'country')`
//   只处理 `Others / Unidentified` 等特殊值的多语言翻译，**不做 alpha-2 → 国家名转换**。
//   国家全名转换需要 cond 字典（动态、按用户切换），而 renderHelper 签名 `(item, key) => string`
//   没有外部上下文参数，无法注入 `countryMap`。所以在 dealWithDataFunc 里直接展开为
//   country_name 字符串后，columns 用 `key: 'country'` 直读即可。
//
export const MarketingCompetitorAdComparisonCountry: DownloadConfigInfoType = {
  columns: (item: DownloadParamsType): CommonColumnsType => {
    const gameOrder: string[] = item?.auxiliaryParams?.gameOrder || [];
    const gameMap = item?.auxiliaryParams?.gameMap;
    return [
      { key: 'country', header: 'Country/Region' },
      ...gameOrder.map(gid => ({
        key: `game_${gid}`,
        header: gameName(gameMap, gid),
      })),
      { key: 'total', header: 'Total' },
    ];
  },
  dealWithDataFunc: ({ data, auxiliaryParams }: DownloadParamsType) => {
    const list: ICountryRowItem[] = data || [];
    const gameOrder: string[] = auxiliaryParams?.gameOrder || [];
    const countryMap: any = auxiliaryParams?.countryMap;
    return list.map(row => {
      const code = (row.country_code || '').toLowerCase();
      const obj: Record<string, any> = {
        // 优先用 cond 的 country_name；缺省 fallback 到 alpha-2 大写
        country: countryMap?.[code]?.country_name || code.toUpperCase(),
      };
      gameOrder.forEach(gid => {
        const found = row.games.find(g => g.game_id === gid);
        obj[`game_${gid}`] = found ? found.value : 0;
      });
      obj.total = Number.isFinite(row.total) ? row.total : 0;
      return obj;
    });
  },
};
